import {
    DepartmentContact,
    IgnoredContact,
    addIgnoredContact,
    getDepartmentContacts,
    getIgnoredContacts,
    removeIgnoredContact,
} from '@/lib/api/atendimento/departments'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAdd,
    MdBlock,
    MdChevronLeft,
    MdChevronRight,
    MdPersonOff,
    MdSearch,
    MdUndo,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface IgnoredContactsTabProps {
    departmentId: number
    canEdit: boolean
}

const PAGE_SIZE = 10

/**
 * Contatos que a IA não responde neste setor.
 *
 * Duas listas: quem já escreveu para o setor (para marcar clicando, que é o
 * caminho comum — o número já está ali) e quem já está ignorado. O número
 * avulso existe para o contato que ainda não tem cadastro nenhum.
 */
export function IgnoredContactsTab({
    departmentId,
    canEdit,
}: IgnoredContactsTabProps) {
    const [ignored, setIgnored] = useState<IgnoredContact[]>([])
    const [ignoredPage, setIgnoredPage] = useState(1)
    const [ignoredPages, setIgnoredPages] = useState(1)
    const [ignoredTotal, setIgnoredTotal] = useState(0)
    const [loadingIgnored, setLoadingIgnored] = useState(false)

    const [contacts, setContacts] = useState<DepartmentContact[]>([])
    const [contactsPage, setContactsPage] = useState(1)
    const [contactsPages, setContactsPages] = useState(1)
    const [contactsSearch, setContactsSearch] = useState('')
    const [loadingContacts, setLoadingContacts] = useState(false)

    const [manualPhone, setManualPhone] = useState('')
    const [manualName, setManualName] = useState('')
    const [manualReason, setManualReason] = useState('')
    const [saving, setSaving] = useState(false)

    const loadIgnored = useCallback(
        async (page: number) => {
            setLoadingIgnored(true)
            try {
                const response = await getIgnoredContacts(departmentId, {
                    page,
                    limit: PAGE_SIZE,
                })
                if (response.success && response.result) {
                    setIgnored(response.result.items)
                    setIgnoredPages(response.result.totalPages)
                    setIgnoredTotal(response.result.total)
                }
            } finally {
                setLoadingIgnored(false)
            }
        },
        [departmentId]
    )

    const loadContacts = useCallback(
        async (page: number, search: string) => {
            setLoadingContacts(true)
            try {
                const response = await getDepartmentContacts(departmentId, {
                    page,
                    limit: PAGE_SIZE,
                    search: search || undefined,
                })
                if (response.success && response.result) {
                    setContacts(response.result.items)
                    setContactsPages(response.result.totalPages)
                }
            } finally {
                setLoadingContacts(false)
            }
        },
        [departmentId]
    )

    useEffect(() => {
        loadIgnored(ignoredPage)
    }, [loadIgnored, ignoredPage])

    // Debounce da busca: cada tecla dispararia uma consulta ao servidor.
    useEffect(() => {
        const timer = setTimeout(() => {
            loadContacts(contactsPage, contactsSearch)
        }, 350)
        return () => clearTimeout(timer)
    }, [loadContacts, contactsPage, contactsSearch])

    const refreshBoth = async () => {
        await Promise.all([
            loadIgnored(ignoredPage),
            loadContacts(contactsPage, contactsSearch),
        ])
    }

    const handleIgnoreContact = async (contact: DepartmentContact) => {
        setSaving(true)
        try {
            const response = await addIgnoredContact(departmentId, {
                phone: contact.phone,
                clientId: contact.clientId,
                contactName: contact.name,
            })
            if (response.success) {
                toast.success('A IA não responde mais este contato neste setor')
                await refreshBoth()
            } else {
                toast.error(response.error || 'Erro ao ignorar contato')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleAddManual = async () => {
        const phone = manualPhone.replace(/\D/g, '')
        if (phone.length < 10) {
            toast.error('Informe o telefone com DDD')
            return
        }

        setSaving(true)
        try {
            const response = await addIgnoredContact(departmentId, {
                phone,
                contactName: manualName.trim() || null,
                reason: manualReason.trim() || null,
            })
            if (response.success) {
                toast.success('Contato adicionado à lista de ignorados')
                setManualPhone('')
                setManualName('')
                setManualReason('')
                await refreshBoth()
            } else {
                toast.error(response.error || 'Erro ao adicionar contato')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleRemove = async (contact: IgnoredContact) => {
        setSaving(true)
        try {
            const response = await removeIgnoredContact(departmentId, contact.id)
            if (response.success) {
                toast.success('A IA volta a responder este contato')
                await refreshBoth()
            } else {
                toast.error(response.error || 'Erro ao remover contato')
            }
        } finally {
            setSaving(false)
        }
    }

    const pager = (
        page: number,
        pages: number,
        onChange: (page: number) => void
    ) =>
        pages > 1 && (
            <div className="flex items-center justify-end gap-2 pt-2">
                <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    disabled={page <= 1}
                    onClick={() => onChange(page - 1)}
                >
                    <MdChevronLeft className="text-base" />
                </button>
                <span className="text-xs text-base-content/60">
                    {page} de {pages}
                </span>
                <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    disabled={page >= pages}
                    onClick={() => onChange(page + 1)}
                >
                    <MdChevronRight className="text-base" />
                </button>
            </div>
        )

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-warning/30 bg-warning/5 px-4 py-3 text-sm text-base-content/70">
                A conversa continua acontecendo normalmente: a mensagem é
                gravada, o atendimento aparece na tela e a equipe é notificada.
                O que muda é que a IA não responde estes contatos neste setor.
            </div>

            {canEdit && (
                <section className="space-y-3 rounded-xl border border-base-300 bg-base-100 p-4">
                    <div>
                        <h3 className="text-sm font-semibold">
                            Adicionar um número
                        </h3>
                        <p className="text-xs text-base-content/60">
                            Para quem ainda não conversou com este setor ou não
                            tem cadastro
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="form-control">
                            <label className="label pb-1">
                                <span className="label-text text-xs font-medium">
                                    Telefone com DDD
                                </span>
                            </label>
                            <input
                                type="tel"
                                className="input input-bordered input-sm w-full"
                                placeholder="73 98863-1174"
                                value={manualPhone}
                                onChange={e => setManualPhone(e.target.value)}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label pb-1">
                                <span className="label-text text-xs font-medium">
                                    Nome (opcional)
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="Como identificar este contato"
                                value={manualName}
                                onChange={e => setManualName(e.target.value)}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label pb-1">
                                <span className="label-text text-xs font-medium">
                                    Motivo (opcional)
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="Ex.: representante comercial"
                                value={manualReason}
                                onChange={e => setManualReason(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm gap-2"
                            disabled={saving || !manualPhone.trim()}
                            onClick={handleAddManual}
                        >
                            <MdAdd className="text-base" />
                            Adicionar à lista
                        </button>
                    </div>
                </section>
            )}

            <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h3 className="text-sm font-semibold">
                            Ignorados pela IA
                        </h3>
                        <p className="text-xs text-base-content/60">
                            {ignoredTotal === 0
                                ? 'Nenhum contato ignorado neste setor'
                                : `${ignoredTotal} contato(s)`}
                        </p>
                    </div>
                </div>

                {loadingIgnored ? (
                    <div className="flex justify-center py-6">
                        <span className="loading loading-spinner loading-md" />
                    </div>
                ) : ignored.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-base-300 px-4 py-6 text-center text-sm text-base-content/50">
                        A IA responde todos os contatos deste setor
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-base-300">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Contato</th>
                                    <th>Telefone</th>
                                    <th>Motivo</th>
                                    <th>Adicionado por</th>
                                    {canEdit && <th className="w-32"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {ignored.map(contact => (
                                    <tr key={contact.id}>
                                        <td className="font-medium">
                                            {contact.name || 'Sem nome'}
                                        </td>
                                        <td>{contact.phoneFormatted}</td>
                                        <td className="text-base-content/60">
                                            {contact.reason || '-'}
                                        </td>
                                        <td className="text-base-content/60">
                                            {contact.createdBy || '-'}
                                        </td>
                                        {canEdit && (
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs gap-1"
                                                    disabled={saving}
                                                    onClick={() =>
                                                        handleRemove(contact)
                                                    }
                                                    title="A IA volta a responder este contato"
                                                >
                                                    <MdUndo className="text-base" />
                                                    Voltar a responder
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {pager(ignoredPage, ignoredPages, setIgnoredPage)}
            </section>

            <section className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">
                        Contatos deste setor
                    </h3>
                    <p className="text-xs text-base-content/60">
                        Quem já conversou aqui. Clique para a IA parar de
                        responder.
                    </p>
                </div>

                <label className="input input-bordered input-sm flex items-center gap-2">
                    <MdSearch className="text-base text-base-content/40" />
                    <input
                        type="text"
                        className="grow"
                        placeholder="Buscar por nome ou telefone..."
                        value={contactsSearch}
                        onChange={e => {
                            setContactsPage(1)
                            setContactsSearch(e.target.value)
                        }}
                    />
                </label>

                {loadingContacts ? (
                    <div className="flex justify-center py-6">
                        <span className="loading loading-spinner loading-md" />
                    </div>
                ) : contacts.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-base-300 px-4 py-6 text-center text-sm text-base-content/50">
                        {contactsSearch
                            ? 'Nenhum contato encontrado'
                            : 'Ninguém conversou com este setor ainda'}
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-base-300">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Contato</th>
                                    <th>Telefone</th>
                                    {canEdit && <th className="w-28"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map(contact => (
                                    <tr key={contact.chatId}>
                                        <td className="font-medium">
                                            {contact.name || 'Sem nome'}
                                        </td>
                                        <td>{contact.phoneFormatted}</td>
                                        {canEdit && (
                                            <td>
                                                {contact.ignored ? (
                                                    <span className="inline-flex items-center gap-1 text-xs text-base-content/50">
                                                        <MdPersonOff className="text-sm" />
                                                        Ignorado
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs gap-1"
                                                        disabled={saving}
                                                        onClick={() =>
                                                            handleIgnoreContact(
                                                                contact
                                                            )
                                                        }
                                                    >
                                                        <MdBlock className="text-base" />
                                                        Ignorar
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {pager(contactsPage, contactsPages, setContactsPage)}
            </section>
        </div>
    )
}
